import { useMemo } from 'react';
import { useDeliverableQuery, useSpecificationVersionsQuery } from '../hooks';
import type { SpecificationVersionsQuery } from '../generated/graphql';

type UseDeliverableVersionsProps = {
  deliverableKey?: string;
  specificationKey?: string;
};

type Deliverable =
  SpecificationVersionsQuery['specification']['deliverables']['nodes'][number];

const formatVersion = (
  external: number | null | undefined,
  internal: number | null | undefined,
): string => {
  if (external === 0) return '0'; // Prevent version being 0.0
  return [external, internal]
    .filter((number) => typeof number === 'number')
    .join('.');
};

const mapDeliverableVersion = (
  d: Deliverable,
  specificationKey?: string | null,
  isSourceVariation: boolean = false,
  revisionsCount?: number,
) => {
  const isExternal = d.hasReachedReviewStage;
  return {
    key: d.key,
    verdict: d.verdict,
    version: formatVersion(d.versionExternal, d.versionInternal),
    versionExternal: d.versionExternal?.toString(),
    deliveredAt:
      (isExternal ? d.externallyDeliveredAt : d.internallyDeliveredAt) ??
      undefined,
    revisionsCount: revisionsCount ?? d.revisions.count,
    isExternal,
    isSourceVariation,
    specificationKey,
  };
};

export const useDeliverableVersions = ({
  deliverableKey,
  specificationKey,
}: UseDeliverableVersionsProps) => {
  const { data: deliverableData } = useDeliverableQuery({
    variables: {
      deliverableKey: deliverableKey || '',
    },
    skip: !deliverableKey,
  });
  const targetSpecKey =
    specificationKey ||
    deliverableData?.deliverable?.specificationKey;

  const { data: specificationData } = useSpecificationVersionsQuery({
    variables: {
      key: targetSpecKey || '',
    },
    skip: !targetSpecKey,
  });

  return useMemo(() => {
    if (!specificationData?.specification) return undefined;

    const { deliverables, sourceDeliverable, revisions } =
      specificationData.specification;

    let versions = deliverables.nodes.map((d) =>
      mapDeliverableVersion(d, targetSpecKey),
    );

    if (sourceDeliverable) {
      versions = [
        ...versions,
        mapDeliverableVersion(
          {
            ...sourceDeliverable,
            versionExternal: 0,
            versionInternal: 0,
          },
          targetSpecKey,
          true,
          revisions?.count ?? 0,
        ),
      ];
    }

    return versions;
  }, [specificationData?.specification, targetSpecKey]);
};
